import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #resolveNextPagePromise = null;
  #resolvePreviousPagePromise = null;
  #aspectRatioHelper;
  #isLoadingNext = false;

  connectedCallback() {
    super.connectedCallback();

    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');

    if (this.#isTargetCollection()) {
      this.#observeInfiniteNextOnly();
    } else {
      this.#observeViewMore();
    }

    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) this.infinityScrollObserver.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #isTargetCollection() {
    const text = `${window.location.pathname} ${document.title}`.toLowerCase();
    return text.includes('plushmeow') || text.includes('meowmeow');
  }

  #observeInfiniteNextOnly() {
    const { grid } = this.refs;
    if (!grid) return;

    let sentinel = this.querySelector('[data-infinite-scroll-sentinel]');

    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.setAttribute('data-infinite-scroll-sentinel', 'true');
      sentinel.style.width = '100%';
      sentinel.style.height = '1px';
      sentinel.style.pointerEvents = 'none';
      this.appendChild(sentinel);
    }

    const { viewMoreNext, viewMorePrevious } = this.refs;
    if (viewMoreNext) viewMoreNext.style.display = 'none';
    if (viewMorePrevious) viewMorePrevious.style.display = 'none';

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (entry.isIntersecting && !this.#isLoadingNext) {
            this.#isLoadingNext = true;
            await this.#renderNextPage();
            this.#isLoadingNext = false;
          }
        }
      },
      {
        rootMargin: '1000px',
      }
    );

    this.infinityScrollObserver.observe(sentinel);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  async #fetchPage(type) {
    const page = this.#getPage(type);

    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#hideInfiniteTrigger();
      return;
    }

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.replaceState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    const afterNextPage = this.#getPage('next');
    if (!afterNextPage || !this.#shouldUsePage(afterNextPage)) {
      this.#hideInfiniteTrigger();
    }
  }

  #hideInfiniteTrigger() {
    const sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
    if (sentinel) sentinel.style.display = 'none';

    const { viewMoreNext } = this.refs;
    if (viewMoreNext) viewMoreNext.style.display = 'none';

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;

      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    if (!cards.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    if (!currentCardPage) return;

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  #handleFilterUpdate = () => {
    this.pages.clear();

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const currentLastPage = this.refs.grid?.dataset.lastPage;

    const observer = new MutationObserver(() => {
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        if (!this.isConnected) return;

        if (this.infinityScrollObserver) {
          this.infinityScrollObserver.disconnect();
          this.infinityScrollObserver = undefined;
        }

        if (this.#isTargetCollection()) {
          this.#observeInfiniteNextOnly();
        } else {
          this.#observeViewMore();
        }

        this.#fetchPage('next');
      }
    });

    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => {
        observer.disconnect();
      }, 3000);
    }
  };
}